import React, { useEffect, useState } from "react";
import {
  useAuthWithRoute,
  useDataWithRoute,
  useTitleFromComponent
} from "../../../utils/hooks";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { VsiBubble, VsiH4, VsiHr } from "../../../styled/App.styled";
import Grid from "../../../components/Grid";
import { filesByLastUsedDefs } from "../../../utils/gridColumnDefs/files/filesByLastUsed";
import ThreeDeeBubbleChart from "../../../components/3DBubbleChart";
import PieChart from "../../../components/PieChart";
import { snakeCaseToTitleCase } from "../../../utils/Helpers";
import { Button, Card, Row, Col, Tooltip } from "reactstrap";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import { PostAuth, PutAuth } from "../../../utils/Verbs";
import styles from "../../../utils/themeStyles";
import { useSearchParams } from "react-router-dom";

const chartMetrics = [
  {
    value: "all_shares",
    label: "All Shares",
    route: "file_analysis_detail_proc",
    yAxis: "GiB"
  },
  {
    value: "shares",
    label: "Shares",
    route: "file_trend_by_share_proc",
    yAxis: "GiB"
  }
];

export default function FileByLastUsed({ context, title }) {
  useTitleFromComponent(`${context} / ${title}`);
  const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [bubbleData, setBubbleData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [categories, setCategories] = useState([]);
  const metric = searchParams.get("metric") || "all_shares";
  let defaultMetric =
    chartMetrics.find(({ value }) => metric === value) || chartMetrics[0];
  const [chartMetric, setChartMetric] = useState(defaultMetric);
  const [additionalChartOptionLabel, setAdditionalChartOptionLabel] =
    useState("");
  const [shareOptions, setShareOptions] = useState([]);
  const [additionalChartOption, setAdditionalChartOption] = useState([]);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState("Save List of Shares");
  const [gridData, setGridData] = useState([]);

  const [savedChangesData] = useAuthWithRoute(
    `/api/v6/admin/file_shares_list/${user._id}/${client.clientId}/by_used_date`
  );
  const [{ recordsets }, { isLoading }] = useDataWithRoute(
    `/api/v6/report/file_analysis_detail_proc?client_id=${client.clientId}&period_id=${periodId}`
  );

  const sortDataIntoBuckets = (data) => {
    let defaultBucket = {
      text_size: 0,
      text_count: 0,
      av_size: 0,
      av_count: 0,
      db_size: 0,
      db_count: 0,
      compress_size: 0,
      compress_count: 0,
      custom_size: 0,
      custom_count: 0,
      other_size: 0,
      other_count: 0,
      total_size: 0,
      total_count: 0
    };

    let buckets = {
      "< 30 Days": defaultBucket,
      "1 - 3 Months": defaultBucket,
      "3 - 6 Months": defaultBucket,
      "6 - 12 Months": defaultBucket,
      "1 - 2 Years": defaultBucket,
      "Over 2 Years": defaultBucket
    };

    let rangeMapping = {
      // KEY: field value of the bucket we possibly get from proc
      // VALUE: which bucket it should map to
      "Over 1 Year": "1 - 2 Years",
      30: "< 30 Days",
      90: "1 - 3 Months",
      180: "3 - 6 Months",
      365: "6 - 12 Months",
      730: "1 - 2 Years",
      older: "Over 2 Years"
    };

    data.forEach((datum) => {
      let ableToMap = Object.keys(rangeMapping).includes(datum.bucket);
      if (datum.bucket in buckets || ableToMap) {
        let bucketRange = ableToMap ? rangeMapping[datum.bucket] : datum.bucket;
        let chosenBucket = buckets[bucketRange];
        let result = {};

        for (const key of Object.keys(chosenBucket)) {
          result[key] = (chosenBucket[key] || 0) + (datum[key] || 0);
        }
        buckets[bucketRange] = result;
      }
    });

    for (let i = 0; i < Object.keys(buckets).length; i++) {
      buckets[Object.keys(buckets)[i]] = {
        ...buckets[Object.keys(buckets)[i]],
        bucket: Object.keys(buckets)[i]
      };
    }

    let bucketData = [
      buckets["< 30 Days"],
      buckets["1 - 3 Months"],
      buckets["3 - 6 Months"],
      buckets["6 - 12 Months"],
      buckets["1 - 2 Years"],
      buckets["Over 2 Years"]
    ];
    if (!data.length) {
      updateBubbleDataWithData([]);
      setGridData([]);
    } else {
      updateBubbleDataWithData(bucketData);
      setGridData(bucketData);
    }
  };

  const updateBubbleDataWithData = (data) => {
    const updatedData = [];
    const updatedPieData = [];
    const categories = [];
    const totals = [];

    const allTypes = {
      db: [],
      text: [],
      av: [],
      compress: [],
      other: []
    };

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      categories.push(datum.bucket);
      totals.push([datum.bucket, parseInt(datum["total_count"])]);
      for (let key in allTypes) {
        allTypes[key].push([
          datum.bucket,
          parseInt(datum[`${key}_count`]),
          parseFloat(datum[`${key}_size`].toFixed(2))
        ]);
      }
    }

    for (let key in allTypes) {
      updatedData.push({
        name: snakeCaseToTitleCase(key),
        data: allTypes[key],
        tooltip: {
          pointFormat:
            "{series.name} Size: <b>{point.z}</b><br/>{series.name} Count: <b>{point.y}</b>"
        }
      });
    }

    updatedPieData.push({ data: totals });

    setPieData(updatedPieData);
    setBubbleData(updatedData);
    setCategories(categories);
  };

  const onMetricChange = async (metric) => {
    setChartMetric(metric);
    searchParams.set("metric", metric.value);
    setSearchParams(searchParams, { replace: true });
  };

  const onAdditionalOptionChange = async (option) => {
    setAdditionalChartOption(option);
    let newData = [];
    for (let i = 0; i < recordsets?.[0]?.length; i++) {
      for (let j = 0; j < option.length; j++) {
        if (recordsets?.[0]?.[i].share_name === option[j]?.value) {
          newData.push(recordsets?.[0][i]);
        }
      }
    }

    sortDataIntoBuckets(newData || []);
  };

  const savePlan = async () => {
    let savedPlan = {
      user: user._id,
      clientId: client.clientId,
      type: "by_used_date",
      sharesList: additionalChartOption
    };
    const token = getAccessTokenSilently();

    if (savedChangesData.length > 0) {
      if (savedChangesData[0].sharesList !== savedPlan.sharesList) {
        try {
          await PutAuth(
            `/api/v6/admin/file_shares_list/${savedChangesData[0]._id}`,
            savedPlan,
            token
          );
          setTooltipMessage("Saved!");
        } catch (err) {
          console.error(err);
        }
      }
    } else {
      try {
        await PostAuth(`/api/v6/admin/file_shares_list`, savedPlan, token);
        setTooltipMessage("Saved!");
      } catch (err) {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    if (chartMetric?.value === "shares") {
      let remapData = (recordsets?.[1] || []).map((datum) => {
        return { value: datum.share_name, label: datum.share_name };
      });

      let chartOptions;
      if (
        savedChangesData?.[0]?.sharesList?.length &&
        !additionalChartOption.length
      ) {
        chartOptions = savedChangesData[0].sharesList;
      } else if (!additionalChartOption.length) {
        chartOptions = [
          remapData[0],
          remapData[1],
          remapData[2],
          remapData[3],
          remapData[4]
        ];
      } else {
        chartOptions = additionalChartOption;
      }

      let newData = [];
      for (let i = 0; i < recordsets?.[0]?.length; i++) {
        for (let j = 0; j < chartOptions.length; j++) {
          if (recordsets?.[0]?.[i].share_name === chartOptions[j]?.value) {
            newData.push(recordsets?.[0][i]);
          }
        }
      }

      sortDataIntoBuckets(newData || []);
      setAdditionalChartOptionLabel("Shares");
      setShareOptions(remapData);
      setAdditionalChartOption(chartOptions);
    } else {
      sortDataIntoBuckets(recordsets?.[0] || []);
      setAdditionalChartOptionLabel("");
      setShareOptions([]);
    }
  }, [recordsets, metric]);

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Files By Last Used</VsiH4>
      <VsiHr />
      <VsiBubble noAutobot>
        <div className={"w-100 p-2 d-flex"}>
          <VsiLabeledSelect
            options={chartMetrics}
            isLoading={isLoading}
            isDisabled={isLoading}
            label={"Metric"}
            minWidth={200}
            maxWidth={500}
            onChange={onMetricChange}
            defaultValue={chartMetric}
          />
          {additionalChartOptionLabel && (
            <>
              <VsiLabeledSelect
                options={shareOptions}
                isLoading={isLoading}
                isDisabled={isLoading}
                label="Shares"
                minWidth={200}
                maxWidth={600}
                onChange={onAdditionalOptionChange}
                defaultValue={additionalChartOption}
                isMulti
              />
              <Button
                className={"h-50 d-inline-block"}
                style={{ marginTop: "38px" }}
                onClick={savePlan}
                id="saveSharesTooltip"
              >
                <i className={`fa-solid fa-floppy-disk`} />
              </Button>
              <Tooltip
                style={{
                  color: styles[theme].textColor,
                  backgroundColor: styles[theme].altBgColor
                }}
                isOpen={tooltipOpen}
                placement="right"
                target="saveSharesTooltip"
                toggle={() => {
                  setTooltipOpen(!tooltipOpen);
                  setTooltipMessage("Save List of Shares");
                }}
              >
                {tooltipMessage}
              </Tooltip>
            </>
          )}
        </div>
      </VsiBubble>
      <VsiBubble noAutobot className={"mb-3"}>
        <Row>
          <Col>
            <ThreeDeeBubbleChart
              series={bubbleData}
              categories={categories}
              includeLegend={true}
              title={"File Size/Count by Last Accessed"}
              config={{
                yAxis: {
                  title: {
                    text: "Count"
                  }
                }
              }}
            />
          </Col>
          <Col>
            <PieChart
              series={pieData}
              disableLabels={false}
              dataLabels={{
                format: "{point.name} ({point.y:,f})"
              }}
            />
          </Col>
        </Row>
      </VsiBubble>
      <VsiBubble>
        <Grid data={gridData} columnDefs={filesByLastUsedDefs} />
      </VsiBubble>
    </Card>
  );
}
